import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Clock, Loader2, AlertCircle, Mail, Calendar, User, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { reportsApi, getErrorMessage } from '../../services/api';
import { formatINR } from '../command-center/constants';
import { useCanSeeRevenue } from '../../lib/perms';
import { toast } from 'sonner';
import { format } from 'date-fns';

const WINDOW_TABS = [
  { key: 6, label: '6m' },
  { key: 12, label: '12m' },
  { key: 24, label: '24m' },
];

// Color scale based on TAT severity (in days). Anything > 60 days is a red flag
// for a staffing op — likely client onboarding paperwork is broken.
const tatToneClass = (days) => {
  if (days == null) return 'bg-slate-200';
  if (days <= 14) return 'bg-emerald-500';
  if (days <= 30) return 'bg-teal-500';
  if (days <= 45) return 'bg-amber-500';
  if (days <= 60) return 'bg-orange-500';
  return 'bg-rose-500';
};

/**
 * Slowest joining clients — horizontal bar chart on Reports page.
 *
 * Surfaces clients with the longest Avg Offer → Join days so DMs can have
 * focused onboarding-velocity conversations with them.
 *
 * Uses `manual_days` (offer_letter_date based) when at least one application
 * has it; falls back to `auto_days` (stage-change timestamp) per row. Each
 * row tags which signal is being shown.
 */
const SlowestJoiningClients = () => {
  const canSeeRevenue = useCanSeeRevenue();
  const [months, setMonths] = React.useState(12);
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  // Drill-down state — which client's per-application details are open
  const [drillClient, setDrillClient] = React.useState(null);
  const [drillLoading, setDrillLoading] = React.useState(false);
  const [drillData, setDrillData] = React.useState(null);

  const openDrill = (row) => {
    setDrillClient(row);
    setDrillData(null);
    setDrillLoading(true);
    reportsApi
      .offerToJoinClientDetails(row.client_id, months)
      .then((res) => setDrillData(res.data || null))
      .catch((err) => toast.error(getErrorMessage(err, 'Failed to load client details')))
      .finally(() => setDrillLoading(false));
  };

  const closeDrill = () => {
    setDrillClient(null);
    setDrillData(null);
  };

  const fmtDay = (d) => {
    if (!d) return '—';
    try {
      return format(new Date(d), 'd MMM yyyy');
    } catch {
      return d;
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reportsApi
      .offerToJoinByClient(months, 10)
      .then((res) => {
        if (!cancelled) setData(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(getErrorMessage(err, 'Failed to load slowest joining clients'));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [months]);

  const rows = data?.rows || [];
  const maxDays = React.useMemo(() => {
    let mx = 0;
    rows.forEach((r) => {
      const v = r.manual_days != null ? r.manual_days : r.auto_days != null ? r.auto_days : 0;
      if (v > mx) mx = v;
    });
    // Pad slightly so the bars don't kiss the edge.
    return Math.max(mx * 1.1, 14);
  }, [rows]);

  const totalApps = data?.total_apps || 0;

  return (
    <Card data-testid="slowest-joining-clients-card">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="w-4 h-4 text-rose-600" />
          Slowest joining clients
          <span className="text-xs font-normal text-slate-500 ml-1">
            · {totalApps} join{totalApps === 1 ? '' : 's'} · last {months}m
          </span>
        </CardTitle>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="sjc-window-tabs">
          {WINDOW_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setMonths(t.key)}
              className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                months === t.key
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`sjc-window-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading client TAT…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm flex flex-col items-center gap-2" data-testid="sjc-empty">
            <AlertCircle className="w-5 h-5 text-slate-300" />
            <div>No joined applications in this window yet.</div>
            <div className="text-[10px] italic">
              Once candidates start joining, this chart ranks clients by how slowly offers turn into joinings.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="sjc-rows">
            {rows.map((r) => {
              // Prefer real (manual) when present, otherwise activity (auto).
              const useManual = r.manual_days != null;
              const days = useManual ? r.manual_days : r.auto_days;
              const widthPct = days != null && maxDays > 0 ? Math.max(2, (days / maxDays) * 100) : 2;
              return (
                <button
                  key={r.client_id}
                  type="button"
                  onClick={() => openDrill(r)}
                  className="grid grid-cols-[minmax(140px,1.8fr)_3fr_auto] items-center gap-3 w-full text-left rounded-md p-1 -m-1 hover:bg-slate-50 transition-colors cursor-pointer"
                  data-testid={`sjc-row-${r.client_id}`}
                  title={`Click to see each joined candidate contributing to ${r.client_name}'s average`}
                >
                  <div
                    className="text-xs font-medium text-slate-700 truncate"
                    title={r.client_name}
                  >
                    {r.client_name}
                  </div>
                  <div className="relative h-5 bg-slate-100 rounded">
                    <div
                      className={`h-full rounded ${tatToneClass(days)} transition-all`}
                      style={{ width: `${widthPct}%` }}
                    />
                    {days != null && (
                      <span className="absolute inset-y-0 left-2 flex items-center text-[10px] font-bold text-white drop-shadow-sm">
                        {days}d
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500 whitespace-nowrap">
                    <span
                      className={`px-1.5 py-0.5 rounded font-bold uppercase tracking-wide ${
                        useManual
                          ? 'bg-teal-100 text-teal-700 border border-teal-200'
                          : 'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}
                      title={
                        useManual
                          ? 'Real benchmark — based on the offer letter date entered by recruiters'
                          : 'Activity benchmark — based on the timestamp when stage moved to Offer Released'
                      }
                    >
                      {useManual ? 'Real' : 'Activity'}
                    </span>
                    <span>· {r.joined_count} join{r.joined_count === 1 ? '' : 's'}</span>
                  </div>
                </button>
              );
            })}
            {/* Legend */}
            <div className="flex items-center gap-3 pt-3 mt-2 border-t border-slate-100 text-[10px] text-slate-500">
              <span className="font-medium uppercase tracking-wide">TAT scale:</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> ≤ 14d</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-teal-500" /> ≤ 30d</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> ≤ 45d</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-orange-500" /> ≤ 60d</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500" /> &gt; 60d</span>
            </div>
          </div>
        )}
      </CardContent>

      {/* Drill-down side panel — per-application breakdown for the picked client */}
      <Sheet open={!!drillClient} onOpenChange={(open) => !open && closeDrill()}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="sjc-drilldown">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-rose-600" />
              {drillClient?.client_name || 'Client'} · Joinings breakdown
            </SheetTitle>
            <SheetDescription className="text-xs">
              Each joined candidate contributing to this client's average TAT, slowest first.
              Last {months} months.
            </SheetDescription>
          </SheetHeader>

          {drillLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading details…
            </div>
          ) : !drillData || drillData.rows.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              No detailed records found for this client.
            </div>
          ) : (
            <div className="mt-4 space-y-2" data-testid="sjc-drill-rows">
              {drillData.rows.map((r) => {
                const useManual = r.manual_days != null;
                const days = useManual ? r.manual_days : r.auto_days;
                return (
                  <div
                    key={r.application_id}
                    className="rounded-lg border border-slate-200 p-3 bg-white hover:bg-slate-50/60 transition-colors"
                    data-testid={`sjc-drill-row-${r.application_id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          <span className="font-semibold text-sm text-slate-900 truncate">
                            {r.candidate_name}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {r.job_title}
                          {r.recruiter_name && r.recruiter_name !== '—' ? (
                            <span> · Recruited by <span className="text-slate-600 font-medium">{r.recruiter_name}</span></span>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                        <div
                          className={`px-2 py-0.5 rounded text-xs font-bold ${tatToneClass(days)} text-white`}
                          title="Offer → Join days"
                        >
                          {days != null ? `${days}d` : '—'}
                        </div>
                        <span
                          className={`text-[9px] uppercase tracking-wide font-bold px-1 py-0 rounded ${
                            useManual
                              ? 'text-teal-700 bg-teal-50 border border-teal-200'
                              : 'text-slate-600 bg-slate-50 border border-slate-200'
                          }`}
                        >
                          {useManual ? 'Real' : 'Activity'}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div className="flex items-center gap-1 text-slate-600">
                        <Calendar className="w-3 h-3 text-teal-600" />
                        <span className="text-slate-400">Offer letter:</span>
                        <span className="font-medium">{fmtDay(r.offer_letter_date) }</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Calendar className="w-3 h-3 text-emerald-600" />
                        <span className="text-slate-400">Joined:</span>
                        <span className="font-medium">{fmtDay(r.joining_date)}</span>
                      </div>
                      {r.offer_released_at ? (
                        <div className="flex items-center gap-1 text-slate-500 col-span-2">
                          <Mail className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-400">Stage→Offered:</span>
                          <span>{fmtDay(r.offer_released_at)}</span>
                          {r.auto_days != null ? (
                            <span className="ml-1 text-[10px] text-slate-400">({r.auto_days}d activity)</span>
                          ) : null}
                        </div>
                      ) : null}
                      {r.offered_ctc && canSeeRevenue ? (
                        <div className="flex items-center gap-1 text-slate-500 col-span-2">
                          <span className="text-slate-400">Offered CTC:</span>
                          <span className="font-medium text-emerald-700">{formatINR(r.offered_ctc)}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </Card>
  );
};

export default SlowestJoiningClients;
